import type { Worm } from '../entities/worm';
import { WA_PHYSICS } from './constants';

const JETPACK_THRUST = 600;
const JETPACK_HORIZONTAL_SPEED = 150;

export interface JetpackManagerDeps {
  input: {
    charging: boolean;
    left: boolean;
    right: boolean;
  };
  terrain: { isSolid: (x: number, y: number) => boolean };
  playSound: (sound: 'jetpackStart' | 'jetpackLoop' | 'jetpackFinish') => void;
  stopSound: (sound: string) => void;
  onTurnEnd: () => void;
}

export class JetpackManager {
  constructor(private deps: JetpackManagerDeps) {}

  handleJetpackInput(deltaTime: number, worm: Worm): void {
    worm.jetpackFuel -= deltaTime;

    if (worm.jetpackFuel <= 0) {
      this.deactivateJetpack(worm);
      return;
    }

    worm.parachuteOpen = false;

    const thrusting = this.deps.input.charging;
    if (thrusting) {
      worm.velocity.y -= JETPACK_THRUST * deltaTime;
      worm.isGrounded = false;
    }

    if (worm.isGrounded && !thrusting) {
      return;
    }

    if (this.deps.input.left) {
      worm.velocity.x = -JETPACK_HORIZONTAL_SPEED;
      worm.facing = 'left';
    } else if (this.deps.input.right) {
      worm.velocity.x = JETPACK_HORIZONTAL_SPEED;
      worm.facing = 'right';
    } else {
      worm.velocity.x *= 0.9;
    }

    worm.velocity.y += WA_PHYSICS.GRAVITY * deltaTime * 0.5;
    worm.velocity.y = Math.max(-300, Math.min(200, worm.velocity.y));
  }

  activateJetpack(worm: Worm): void {
    worm.immuneToFallDamage = true;
    worm.onJetpackDeactivate = () => {
      worm.immuneToFallDamage = false;
      this.deps.stopSound('jetpackLoop');
      this.deps.playSound('jetpackFinish');
      this.deps.onTurnEnd();
    };
    this.deps.playSound('jetpackStart');
    setTimeout(() => {
      if (worm.jetpackActive) {
        this.deps.playSound('jetpackLoop');
      }
    }, 300);
  }

  deactivateJetpack(worm: Worm): void {
    worm.jetpackActive = false;
    worm.jetpackFuel = 0;
    worm.onJetpackDeactivate?.();
  }

  applyJetpackTerrainCollision(worm: Worm): void {
    const width = WA_PHYSICS.WORM.WIDTH;
    const height = WA_PHYSICS.WORM.HEIGHT;
    const centerX = worm.position.x + width / 2;

    if (worm.velocity.x > 0) {
      const rightX = worm.position.x + width;
      if (this.deps.terrain.isSolid(rightX, worm.position.y + height / 2)) {
        worm.velocity.x = 0;
        while (this.deps.terrain.isSolid(worm.position.x + width, worm.position.y + height / 2)) {
          worm.position.x -= 1;
        }
      }
    } else if (worm.velocity.x < 0) {
      if (this.deps.terrain.isSolid(worm.position.x, worm.position.y + height / 2)) {
        worm.velocity.x = 0;
        while (this.deps.terrain.isSolid(worm.position.x, worm.position.y + height / 2)) {
          worm.position.x += 1;
        }
      }
    }

    if (worm.velocity.y < 0) {
      if (this.deps.terrain.isSolid(centerX, worm.position.y)) {
        worm.velocity.y = 0;
        while (this.deps.terrain.isSolid(centerX, worm.position.y)) {
          worm.position.y += 1;
        }
      }
    } else if (worm.velocity.y > 0) {
      const feetY = worm.position.y + height;
      if (this.deps.terrain.isSolid(centerX, feetY)) {
        worm.velocity.y = 0;
        worm.isGrounded = true;
        while (this.deps.terrain.isSolid(centerX, worm.position.y + height)) {
          worm.position.y -= 1;
        }
      } else {
        worm.isGrounded = false;
      }
    }

    worm.position.x += worm.velocity.x * (1 / 60);
    worm.position.y += worm.velocity.y * (1 / 60);
  }
}
